import { GroupAction } from '@assets/icons';
import AuditsTable from '@components/auditsTable/AuditsTable';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import { Colors } from '@constants/colors.constants';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import { Button, CardActionArea, Stack, Typography } from '@mui/material';
import { useEffectFn } from '@ngneat/effects-hooks';
import { useObservable } from '@ngneat/react-rxjs';
import ActivateAuditModal from '@screens/admin/audits/audits-offer-activation/ActivateAudit.modal';
import GroupActionModal from '@screens/admin/audits/audits-offer-activation/GroupAction.modal';
import GroupModal from '@screens/admin/audits/audits-offer-activation/GroupModal';
import ContainerDetails from '@screens/admin/audits/audits-offer-formalization/ContainerDetails';
import ExistingContractModal from '@screens/admin/clients/contracts/ExistingContract.modal';
import { AuditOfferResponseExpendedDto } from '@store/audits/audit-offer/audit-offer.model';
import { AuditsOfferEffect } from '@store/audits/audit-offer/audits-offer.effects';
import { ServiceKindTypeEnum } from '@store/settings/serviceKinds';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { finalize } from 'rxjs';
import { auditsOfferService } from '../../../../../store/audits/audit-offer/audit-offer.services';
import { auditsofferQuery } from '../../../../../store/audits/audit-offer/audits-offer.query';

export interface EngagedSupplierData {
  id: number;
  supplier: string;
  phone: string;
  email: string;
  city: string;
  country: string;
  supplierNbr: number;
}

enum ActionType {
  GROUPEMODAL = 'groupModal',
  GROUPEDACTIONMODAL = 'groupActionModal',
  CONTAINERDETAILS = 'containerDetails',
  ACTIVATE = 'activate',
  CONTRACT = 'contract',
}

const boxStyle = {
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: Colors.primary,
  width: '40px',
  height: '40px',
  borderRadius: '4px',
};

const boxStyleEmpty = {
  justifyContent: 'center',
  alignItems: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '4px',
  border: '1px solid #F4F6F8',
};


const AuditsOfferActivation = () => {
  const { t } = useTranslation();
  const [Opentab, setOpentab] = useState<ActionType>();
  const handleCloseModal = () => setOpentab(undefined);
  const [selectedProposalRow, setSelectedProposalRow] = useState<number[]>([]);
  const [selectContainerId, setSelectContainerId] = useState<number>();
  const [loading, setLoading] = useState<boolean>(true);
  const { auditId } = useParams();
  const [{ auditsOffers }] = useObservable(auditsofferQuery.auditsoffers$);
  const { enqueueSnackbar } = useSnackbar();
  const [contract] = useObservable(auditsofferQuery.contract$);

  const auditOfferFiltered = auditsOffers?.sort((a, b) => b.auditOperationId - a.auditOperationId);

  const offerAction = useEffectFn(AuditsOfferEffect);
  const [actionObj] = useObservable(auditsofferQuery.actionArray$);


  const [tmpStrMap, setTmpStrMap] = useState({});

  const setTmpStr = (id, value) => {
    setTmpStrMap((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  useEffect(() => {
    if (auditId) {
      setLoading(true);
      auditsOfferService.GETAuditOffersByAudit(auditId)
        .pipe(finalize(() => setLoading(false)))
        .subscribe({
          error: (err) => {
            enqueueSnackbar(err.text, err.options);
          }
        });
    }
  }, [auditId, enqueueSnackbar]);

  useEffect(() => {
    if (auditId && actionObj.auditOfferIds.length > 0) {
      offerAction({ auditAction: actionObj, auditId });
    }
  }, [actionObj, auditId, offerAction]);

  useEffect(() => {
    if (contract.open) {
      setOpentab(ActionType.CONTRACT);
    }
  }, [contract.open]);

  const handleSelectedRows = (params: any) => setSelectedProposalRow([...params]);
  const handleSelectedDetails = (id: number) => {
    setSelectContainerId(id);
    setOpentab(ActionType.CONTAINERDETAILS);
  };


  const getCurrenModal = () => {
    return {
      [ActionType.GROUPEMODAL]: <GroupModal handleCloseModal={handleCloseModal} selectedProposalRow={selectedProposalRow} />,
      [ActionType.GROUPEDACTIONMODAL]: <GroupActionModal handleCloseModal={handleCloseModal} />,
      [ActionType.CONTAINERDETAILS]: <ContainerDetails onClose={handleCloseModal} auditOperationId={selectContainerId} />,
      [ActionType.ACTIVATE]: <ActivateAuditModal onClose={handleCloseModal} />,
      [ActionType.CONTRACT]: <ExistingContractModal contractId={contract.id as number} handleClose={() => {
        auditsOfferService.setContract({ open: false });
        handleCloseModal();
      }} />,

    };
  };

  const ViewCurrentModal: JSX.Element = useMemo(() =>
    getCurrenModal()[Opentab as ActionType],
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [Opentab]
  );

  return (
    <Stack flex={1} width="100%" py={'20px'} spacing={5} sx={{ backgroundColor: 'white' }} mt='20px' borderRadius='2px' mb='20px'>
      <Stack direction={'row'} spacing={2} alignItems={'center'} justifyContent='space-between' px={'20px'} >
        <Typography fontSize="20px" fontWeight="700" flex={1}>
          {t('audits.global.OfferActivation.title')}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setOpentab(ActionType.GROUPEDACTIONMODAL)}
            startIcon={<GroupAction />}
          >
            {t('global.groupedAction')}
          </Button>
          <Button variant="contained" color="primary" onClick={() => setOpentab(ActionType.ACTIVATE)}>
            {t('audits.OfferActivation.activateOffer.title')}
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={2}>
        <Stack direction='row' px={'20px'} spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setOpentab(ActionType.GROUPEMODAL)}
            startIcon={<GroupAction />}
          >
            {t('audits.OfferActivation.groupedModal.title')}
          </Button>
        </Stack>
        <AuditsTable
          columns={[
            COLUMN_TYPES.TEXT({
              field: 'serviceType',
              label: 'Famille',
              valueFormatter: (value) => ServiceKindTypeEnum.selectItems().find((o) => o.value === value)?.label
            }),
            COLUMN_TYPES.TEXT({
              field: 'serviceFamily',
              label: 'Prestation',
            }),
            COLUMN_TYPES.TEXT({
              field: 'label',
              valueGetter: (params) => params.row.clientWasteName,
              label: 'Libellé de la prestation',
              width: 500,
              renderCell: (params) => {
                return (
                  <TextFieldComponent
                    handleChange={(value) => {
                      setTmpStr(params.row.id, value);
                      return auditsOfferService.setAction({ clientWasteName: value, auditOfferIds: [params.row.id] });
                    }}
                    placeholder="-"
                    value={tmpStrMap[params.row.id] || params.value} />
                );
              }
            }),
            COLUMN_TYPES.TEXT({
              field: 'unitCostInCents',
              label: 'Prix partenaire',
              valueGetter: (params) => params.row.unitCostInCents,
              valueFormatter: (value) => value / 100,
              renderCell: (params) => {
                return (
                  <Stack flex={1}>
                    <Typography>{params.formattedValue} CHF</Typography>
                  </Stack>
                );
              }
            }),
            COLUMN_TYPES.TEXT({
              label: 'Prix client',
              field: 'unitPriceInCents',
              valueGetter: (params) => params.row.unitPriceInCents,
              valueFormatter: (value) => value / 100,
              renderCell: (params) => {
                return (
                  <Stack flex={1}>
                    <Typography>{params.formattedValue} CHF</Typography>
                  </Stack>
                );
              }
            }),
            COLUMN_TYPES.CUSTOM({ label: 'Date de début' }),
            COLUMN_TYPES.CUSTOM({ label: 'Date de fin' }),
            COLUMN_TYPES.ICON({
              field: 'enableSchedule',
              label: 'Recurrence',
              renderIcon: (data: AuditOfferResponseExpendedDto) => {
                console.log(data.serviceType);
                if (data.serviceType === ServiceKindTypeEnum.PICKUP) {
                    return (
                        <Stack
                        component={CardActionArea}
                        onClick={() => auditsOfferService.setAction({ enableSchedule: !data.enableSchedule, auditOfferIds: [data.id] })}
                        style={data.enableSchedule == true ? boxStyle : boxStyleEmpty}>
                        <ManageHistoryIcon sx={{ color: data.enableSchedule ? Colors.black : 'grey' }} />
                        </Stack>
                    );
                }
              },
              width: 40,
            }),
            COLUMN_TYPES.ICON({
              field: 'canBeRequested',
              label: 'Peut être demandé',
              renderIcon: (data: AuditOfferResponseExpendedDto) => {
                return (
                  <Stack
                    component={CardActionArea}
                    onClick={() => auditsOfferService.setAction({ canBeRequested: !data.canBeRequested, auditOfferIds: [data.id] })}
                    style={data.canBeRequested == true ? boxStyle : boxStyleEmpty}>
                    <HelpCenterIcon sx={{ color: data.canBeRequested ? Colors.black : 'grey' }} />
                  </Stack>
                );
              },
              width: 40,
            }),
            COLUMN_TYPES.ICON({
              field: 'contractId',
              label: 'Contrat',
              width: 20,
            }),
          ]}
          handleSelectedRows={handleSelectedRows}
          loading={loading}
          selectedRows={selectedProposalRow}
          enableContainerDetails
          groupedBy='auditOperationId'
          handleSelectedDetails={handleSelectedDetails}
          data={auditOfferFiltered || []}
        />
      </Stack>
      {ViewCurrentModal}
      <Outlet />
    </Stack >
  );
};

export default AuditsOfferActivation;
